import { Directive, HostListener, ElementRef, Input, Output, EventEmitter } from '@angular/core';
import { NgControl } from '@angular/forms';

/**
 * Directive to enforce consistent date validation across all date inputs
 * Prevents invalid dates like 10/10/0202 from being entered
 * Usage: Add [appDateValidation] to any date input field
 */
@Directive({
  selector: '[appDateValidation]'
})
export class DateValidationDirective {
  @Input() dateFormat: string = 'dd/mm/yyyy'; // Default format
  @Output() dateValidationError = new EventEmitter<string>();

  constructor(
    private el: ElementRef,
    private control: NgControl
  ) {}

  @HostListener('blur', ['$event'])
  onBlur(event: any): void {
    const value = event.target.value;
    
    if (!value || value.trim() === '') {
      return; // Allow empty values
    }

    if (!this.isValidDate(value)) {
      // Clear the invalid date
      this.control.control?.setValue('');
      this.el.nativeElement.value = '';
      
      // Emit error event for custom handling if needed
      this.dateValidationError.emit('Invalid date format. Please use dd/mm/yyyy');
      
      // Add visual feedback
      this.el.nativeElement.classList.add('is-invalid');
      setTimeout(() => {
        this.el.nativeElement.classList.remove('is-invalid');
      }, 2000);
    }
  }

  @HostListener('input', ['$event'])
  onInput(event: any): void {
    const value = event.target.value;
    
    // Prevent typing more than expected characters
    if (value && value.length > 10) {
      event.target.value = value.substring(0, 10);
      event.preventDefault();
    }
  }

  @HostListener('change', ['$event'])
  onChange(event: any): void {
    const value = event.target.value;
    
    if (!value || value.trim() === '') {
      return;
    }

    if (!this.isValidDate(value)) {
      // Clear the invalid date immediately on change
      this.control.control?.setValue('');
      this.el.nativeElement.value = '';
      this.dateValidationError.emit('Invalid date format. Please use dd/mm/yyyy');
    }
  }

  /**
   * Comprehensive date validation
   * Checks format, valid day/month/year ranges, and actual date validity
   */
  private isValidDate(dateString: string): boolean {
    if (!dateString) return false;

    // Strict regex for dd/mm/yyyy format
    const dateRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/(\d{4})$/;
    
    if (!dateRegex.test(dateString)) {
      return false;
    }

    // Extract day, month, year
    const parts = dateString.split('/');
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    // Validate year range (prevent years like 0202, 0001, etc.)
    if (year < 1900 || year > 2200) {
      return false;
    }

    // Create date object and verify it's valid
    // Note: JS Date uses 0-indexed months
    const date = new Date(year, month - 1, day);
    
    // Check if the date object represents the same date we parsed
    const isValidDate = date.getDate() === day &&
                       date.getMonth() === month - 1 &&
                       date.getFullYear() === year;

    return isValidDate;
  }
}